import Image, { ImageProps } from 'next/image';
import { useState, useEffect, useRef } from 'react';
import Head from 'next/head';
import styles from './OptimizedHeroImage.module.scss';

// Define the type for fetchPriority
type FetchPriority = 'high' | 'low' | 'auto';

interface ExtendedHTMLLinkElement extends Omit<HTMLLinkElement, 'fetchPriority'> {
  fetchPriority: FetchPriority;
}

interface OptimizedHeroImageProps extends Omit<ImageProps, 'src' | 'alt'> {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  sizes?: string;
  quality?: number;
  preload?: boolean;
  width?: number;
  height?: number;
  blurDataURL?: string;
}

const OptimizedHeroImage = ({
  src,
  alt,
  className = '',
  priority = true,
  sizes = '100vw',
  quality = 75,
  preload = true,
  width,
  height,
  blurDataURL,
  ...props
}: OptimizedHeroImageProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const preloadLinkRef = useRef<ExtendedHTMLLinkElement | null>(null);

  // Use native preload for critical images with proper fetch priority
  useEffect(() => {
    if (priority && preload && typeof window !== 'undefined') {
      // Remove any existing preload link
      if (preloadLinkRef.current && preloadLinkRef.current.parentNode) {
        preloadLinkRef.current.parentNode.removeChild(preloadLinkRef.current);
      }

      // Create new preload link with high fetch priority
      const link = document.createElement('link') as ExtendedHTMLLinkElement;
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.fetchPriority = 'high';
      link.type = 'image/webp'; // Add type hint for modern formats
      document.head.appendChild(link);
      preloadLinkRef.current = link;

      return () => {
        if (preloadLinkRef.current && preloadLinkRef.current.parentNode) {
          preloadLinkRef.current.parentNode.removeChild(preloadLinkRef.current);
          preloadLinkRef.current = null;
        }
      };
    }
  }, [src, priority, preload]);

  // Handle image loading state
  const handleLoad = () => {
    setIsLoaded(true);
    // Reduce the timeout to improve perceived performance
    setTimeout(() => {
      setIsLoading(false);
    }, 50);
  };

  // Calculate aspect ratio for the container
  const aspectRatio = width && height ? `${width}/${height}` : '16/9';

  // Generate responsive sizes based on viewport
  const responsiveSizes = sizes || `(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px`;

  return (
    <>
      {priority && preload && (
        <Head>
          <link
            rel="preload"
            as="image"
            href={src}
            type="image/webp"
          />
        </Head>
      )}
      
      <div 
        className={`${styles.container} ${className}`} 
        data-placeholder-for={src}
        style={{
          aspectRatio,
          backgroundColor: '#f0f0f0'
        }}
      >
        {/* Loading placeholder with blur effect */}
        {isLoading && !isLoaded && (
          <div 
            className={styles.placeholder}
            style={{
              backgroundImage: blurDataURL ? `url(${blurDataURL})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: blurDataURL ? 'blur(20px)' : undefined,
            }}
          />
        )}
        
        <div className={`${styles.imageContainer} ${isLoaded ? styles.loaded : styles.loading}`}>
          <Image
            {...props}
            src={error ? '/fallback-hero.jpg' : src}
            alt={alt}
            priority={priority}
            sizes={responsiveSizes}
            quality={quality}
            className={className}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            onLoad={handleLoad}
            onError={() => setError(true)}
            placeholder={blurDataURL ? 'blur' : 'empty'}
            blurDataURL={blurDataURL}
            style={{
              ...props.style,
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              willChange: 'transform', // Optimize for animations
            }}
          />
        </div>
      </div>
    </>
  );
};

export default OptimizedHeroImage; 